import React, { useCallback, useMemo, useState } from "react";

import { DropdownAutocomplete } from "../DropdownAutocomplete";
import { DropdownOption } from "../DropdownAutocomplete/types";
import { CvhVehicleSearchRow } from "services/api/catalog/cvh/types";
import { JOMDropdownProps } from "./types";
import { usePaginatedJoms } from "./hooks";

// Godina proizvodnje "od - do".
const formatYear = (yearFrom: number | null, yearTo: number | null): string => {
  if (yearFrom && yearTo) return `${yearFrom} - ${yearTo}`;
  if (yearFrom) return `${yearFrom} -`;
  return "";
};

// Sekundarni detalji vozila: snaga, gorivo, paket opreme.
const formatDetails = (row: CvhVehicleSearchRow): string => {
  const power = row.power ? `${row.power} kW` : null;
  return [power, row.fuelType, row.equipment].filter(Boolean).join(" · ");
};

// Redak tooltipa vozila.
const JomTooltipRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between text-sm mb-1 gap-4">
    <span className="text-slate-300">{label}</span>
    <span className="font-medium text-right max-w-[180px] truncate">{value}</span>
  </div>
);

// Bogati tooltip vozila — prikaz na hover slike u dropdownu (kao VehicleDropdown).
const JomIconTooltip: React.FC<{ row: CvhVehicleSearchRow }> = ({ row }) => {
  const year = formatYear(row.yearFrom, row.yearTo);

  return (
    <div className="min-w-[240px]">
      {row.image && (
        <div className="mb-3 rounded overflow-hidden">
          <img src={row.image} alt={row.name} className="w-full h-[120px] object-cover rounded" />
        </div>
      )}
      <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji vozila</div>
      {row.name && <JomTooltipRow label="Naziv" value={row.name} />}
      <JomTooltipRow label="JOM" value={row.jom} />
      {year && <JomTooltipRow label="Godina" value={year} />}
      {row.power != null && <JomTooltipRow label="Snaga" value={`${row.power} kW`} />}
      {row.fuelType && <JomTooltipRow label="Gorivo" value={row.fuelType} />}
      {row.equipment && <JomTooltipRow label="Oprema" value={row.equipment} />}
    </div>
  );
};

const JOMDropdown: React.FC<JOMDropdownProps> = (props: JOMDropdownProps) => {
  const {
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    onSelect,
  } = props;

  const [searchValue, setSearchValue] = useState<string>("");

  const { joms, isLoading, hasMore, loadMore } = usePaginatedJoms(searchValue);

  const handleSearchFocus = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((search: string) => {
    setSearchValue(search);
  }, []);

  const handleChange = useCallback(
    (e: any, changedName: string, item: DropdownOption) => {
      onChange(e, changedName, item);
      onSelect?.(joms.find((row) => row.jom === item.value) ?? null);
    },
    [onChange, onSelect, joms]
  );

  const jomItems: DropdownOption[] = useMemo(() => {
    const options: DropdownOption[] = joms.map((row) => {
      const year = formatYear(row.yearFrom, row.yearTo);
      const details = formatDetails(row);
      return {
        value: row.jom,
        text: (
          <div className="flex items-center justify-between w-full gap-2">
            <span className="flex items-center gap-2 min-w-0">
              <span>{row.jom}</span>
              {row.name && (
                <span className="text-slate-400 text-xs">{row.name}</span>
              )}
              {details && (
                <span className="text-slate-400 text-xs truncate">{details}</span>
              )}
            </span>
            {year && (
              <span className="text-slate-400 text-xs whitespace-nowrap">
                {year}
              </span>
            )}
          </div>
        ),
        image: row.image || undefined,
        tooltip: row.image ? <JomIconTooltip row={row} /> : undefined,
      };
    });

    // Preselektirani JOM koji nije među učitanima — ubaci ga da se prikaže u inputu.
    if (value && !joms.some((row) => row.jom === value)) {
      options.unshift({ value, text: value });
    }

    return options;
  }, [joms, value]);

  // Odabrano vozilo — za tooltip na hover slike odabrane stavke.
  const selectedRow = useMemo(
    () => joms.find((row) => row.jom === value),
    [joms, value]
  );

  return (
    <DropdownAutocomplete
      name={name}
      label={label}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      value={value}
      direction={direction}
      width={width}
      height={height}
      options={jomItems}
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      error={error}
      isLoading={isLoading}
      onLoadMore={hasMore ? loadMore : undefined}
      tooltip={selectedRow?.image ? <JomIconTooltip row={selectedRow} /> : undefined}
    />
  );
};

export default React.memo(JOMDropdown);
